import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Plus, Users, Trash2, Edit, Leaf, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Materials",
  "Equipment",
  "Logistics",
  "Energy",
  "Services",
  "Technology",
  "Other",
];

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    contact_email: "",
    sustainability_score: 50,
    carbon_intensity: 0,
    notes: "",
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error("Failed to fetch suppliers");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category) {
      toast.error("Name and category are required");
      return;
    }

    setSaving(true);
    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, formData);
        toast.success("Supplier updated");
      } else {
        await axios.post(`${API}/suppliers`, formData);
        toast.success("Supplier added");
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error("Failed to save supplier");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      category: supplier.category,
      contact_email: supplier.contact_email || "",
      sustainability_score: supplier.sustainability_score,
      carbon_intensity: supplier.carbon_intensity,
      notes: supplier.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast.success("Supplier deleted");
      fetchSuppliers();
    } catch (error) {
      toast.error("Failed to delete supplier");
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setFormData({
      name: "",
      category: "",
      contact_email: "",
      sustainability_score: 50,
      carbon_intensity: 0,
      notes: "",
    });
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-emerald-600 bg-emerald-50";
    if (score >= 40) return "text-amber-600 bg-amber-50";
    return "text-red-600 bg-red-50";
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return "Excellent";
    if (score >= 40) return "Moderate";
    return "Needs Improvement";
  };

  return (
    <div className="space-y-8" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
            Suppliers
          </h1>
          <p className="text-[#5c6b63] mt-2">
            Manage supplier sustainability profiles
          </p>
        </div>
        <Dialog 
          open={dialogOpen} 
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button className="btn-primary flex items-center gap-2" data-testid="add-supplier-btn">
              <Plus className="w-4 h-4" />
              Add Supplier
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">
                {editingSupplier ? "Edit Supplier" : "Add New Supplier"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Supplier Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Company Name"
                    className="input-field"
                    data-testid="supplier-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="supplier-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="contact@supplier.com"
                  className="input-field"
                  data-testid="supplier-email-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Sustainability Score</Label>
                  <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${getScoreColor(formData.sustainability_score)}`}>
                    {formData.sustainability_score}% - {getScoreLabel(formData.sustainability_score)}
                  </span>
                </div>
                <Slider
                  value={[formData.sustainability_score]}
                  onValueChange={([value]) => setFormData({ ...formData, sustainability_score: value })}
                  max={100}
                  step={1}
                  className="py-2"
                  data-testid="sustainability-score-slider"
                />
              </div>

              <div className="space-y-2">
                <Label>Carbon Intensity (kg CO₂e/£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.carbon_intensity}
                  onChange={(e) => setFormData({ ...formData, carbon_intensity: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="input-field"
                  data-testid="carbon-intensity-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this supplier..."
                  className="input-field min-h-[80px]"
                  data-testid="supplier-notes-input"
                />
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full btn-accent"
                data-testid="save-supplier-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingSupplier ? (
                  "Update Supplier"
                ) : (
                  "Add Supplier"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="p-4">
            <p className="text-sm text-[#5c6b63]">Total Suppliers</p>
            <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">
              {suppliers.length}
            </p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4">
            <p className="text-sm text-[#5c6b63]">Avg Sustainability Score</p>
            <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">
              {suppliers.length > 0
                ? Math.round(
                    suppliers.reduce((acc, s) => acc + s.sustainability_score, 0) /
                      suppliers.length
                  )
                : 0}
              %
            </p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4">
            <p className="text-sm text-[#5c6b63]">High Performers</p>
            <p className="text-2xl font-bold text-emerald-600 font-['Outfit']">
              {suppliers.filter((s) => s.sustainability_score >= 70).length}
            </p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4">
            <p className="text-sm text-[#5c6b63]">Needs Attention</p>
            <p className="text-2xl font-bold text-red-600 font-['Outfit']">
              {suppliers.filter((s) => s.sustainability_score < 40).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Suppliers Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
            All Suppliers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#1a2e22] animate-spin" />
            </div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-[#c8d4cc] mx-auto mb-4" />
              <p className="text-[#5c6b63] mb-2">No suppliers added yet</p>
              <p className="text-sm text-[#5c6b63]">
                Add suppliers to track their sustainability performance
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.map((supplier) => (
                <Card
                  key={supplier.id}
                  className="border border-[#e0e7e3] hover:shadow-md transition-shadow"
                  data-testid={`supplier-card-${supplier.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-[#1a2e22]">{supplier.name}</h3>
                        <span className="text-sm text-[#5c6b63]">{supplier.category}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(supplier)}
                          className="h-8 w-8 text-[#5c6b63] hover:text-[#1a2e22]"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(supplier.id)}
                          className="h-8 w-8 text-[#5c6b63] hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#5c6b63]">Sustainability</span>
                        <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${getScoreColor(supplier.sustainability_score)}`}>
                          {supplier.sustainability_score}%
                        </span>
                      </div>
                      <div className="h-2 bg-[#e0e7e3] rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            supplier.sustainability_score >= 70
                              ? "bg-emerald-500"
                              : supplier.sustainability_score >= 40
                              ? "bg-amber-500"
                              : "bg-red-500"
                          }`}
                          style={{ width: `${supplier.sustainability_score}%` }}
                        />
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-[#e0e7e3]">
                        <div className="flex items-center gap-1 text-sm text-[#5c6b63]">
                          <Leaf className="w-4 h-4" />
                          Carbon Intensity
                        </div>
                        <span className="font-mono text-sm font-medium text-[#1a2e22]">
                          {supplier.carbon_intensity.toFixed(2)} kg/£
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
